import React, { useEffect, useState } from "react";
import api, { formatApiError, API } from "../../lib/api";
import { Window, Loading, EmptyState, Dialog, ConfirmDialog } from "../../components/VB";
import SlipGaji from "../../components/SlipGaji";
import RupiahInput from "../../components/RupiahInput";
import { toast } from "sonner";
import { Printer, Eye, Download, FileSpreadsheet, Wallet, X } from "lucide-react";

const fmt = (n) => "Rp " + (n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export default function Payroll() {
  const today = new Date();
  const defaultBulan = today.toISOString().slice(0, 7);
  const [bulan, setBulan] = useState(defaultBulan);
  const [divs, setDivs] = useState([]);
  const [divId, setDivId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slipView, setSlipView] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount_paid: 0, payment_date: "", payment_method: "Transfer", catatan: "" });
  const [cancelTarget, setCancelTarget] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/divisi");
      setDivs(data);
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { bulan };
      if (divId) params.divisi_id = divId;
      const { data } = await api.get("/payroll/list", { params });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = (format) => {
    const params = new URLSearchParams({ bulan, format });
    if (divId) params.append("divisi_id", divId);
    const token = localStorage.getItem("access_token");
    fetch(`${API}/payroll/list?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `payroll_${bulan}.${format}`;
        a.click();
      })
      .catch(() => toast.error("Gagal download"));
  };

  const openPayDialog = (row) => {
    // Default pay date = 17th of selected month
    const [y, m] = bulan.split("-");
    const payDate = `${y}-${m}-17`;
    setPayTarget(row);
    setPayForm({
      amount_paid: row.gaji_bersih,
      payment_date: payDate,
      payment_method: row.metode_gaji || "Transfer",
      catatan: ""
    });
  };

  const submitPay = async () => {
    try {
      await api.post("/payroll/payments", {
        employee_id: payTarget.employee_id,
        periode_bulan: bulan,
        amount_paid: parseFloat(payForm.amount_paid),
        payment_date: payForm.payment_date,
        payment_method: payForm.payment_method,
        catatan: payForm.catatan,
      });
      toast.success("Pembayaran berhasil dicatat & masuk ke arus kas");
      setPayTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancelPay = async () => {
    try {
      await api.delete(`/payroll/payments/${cancelTarget.payment_id}`);
      toast.success("Pembayaran dibatalkan & cashflow di-rollback");
      setCancelTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (slipView) {
    return (
      <div>
        <div className="no-print" style={{ marginBottom: 8, display: "flex", gap: 6 }}>
          <button className="vb-btn" onClick={() => setSlipView(null)} data-testid="back-list">← Kembali</button>
          <button className="vb-btn-primary" onClick={() => setTimeout(() => window.print(), 100)} data-testid="print-slip">
            <Printer className="w-3 h-3 inline mr-1" />Cetak Slip
          </button>
        </div>
        <SlipGaji data={slipView} />
      </div>
    );
  }

  const sumGajiBersih = rows.reduce((a, r) => a + (r.gaji_bersih || 0), 0);
  const sumPotongan = rows.reduce((a, r) => a + (r.total_potongan || 0), 0);
  const sumTunjangan = rows.reduce((a, r) => a + (r.total_tunjangan || 0), 0);
  const sumPaid = rows.reduce((a, r) => a + (r.amount_paid || 0), 0);
  const countPaid = rows.filter((r) => r.payment_status === "Terbayar").length;
  const countPending = rows.length - countPaid;

  const today_str = new Date().toISOString().slice(0, 10);

  return (
    <Window title={`Payroll - Periode Tgl 17 ${bulan}`}>
      <div className="no-print" style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <input type="month" className="vb-input" style={{ width: 160 }} value={bulan} onChange={(e) => setBulan(e.target.value)} data-testid="payroll-bulan" />
        <select className="vb-select" style={{ width: 160 }} value={divId} onChange={(e) => setDivId(e.target.value)} data-testid="payroll-divisi">
          <option value="">Semua Divisi</option>
          {divs.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
        </select>
        <button className="vb-btn-primary" onClick={load} data-testid="payroll-load">Hitung Payroll</button>
        <button className="vb-btn" onClick={() => download("csv")} data-testid="payroll-csv"><Download className="w-3 h-3 inline mr-1" />CSV</button>
        <button className="vb-btn" onClick={() => download("xlsx")} data-testid="payroll-xlsx"><FileSpreadsheet className="w-3 h-3 inline mr-1" />Excel</button>
      </div>

      {rows.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 8, marginBottom: 10 }}>
          <div className="vb-statcard"><div className="vb-statcard-label">Total Karyawan</div><div className="vb-statcard-value">{rows.length}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Sudah Terbayar</div><div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 18 }}>{countPaid}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Belum Bayar (Pending)</div><div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 18 }}>{countPending}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Total Tunjangan</div><div className="vb-statcard-value" style={{ color: "#003399", fontSize: 16 }}>{fmt(sumTunjangan)}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Total Potongan</div><div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 16 }}>{fmt(sumPotongan)}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Total Gaji Bersih</div><div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 16 }}>{fmt(sumGajiBersih)}</div></div>
          <div className="vb-statcard"><div className="vb-statcard-label">Sudah Dibayarkan</div><div className="vb-statcard-value" style={{ color: "#003399", fontSize: 16 }}>{fmt(sumPaid)}</div></div>
        </div>
      )}

      {loading ? <Loading /> : rows.length === 0 ? <EmptyState message="Klik 'Hitung Payroll' untuk memuat" /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead>
              <tr>
                <th>No. Karyawan</th><th>Nama</th>
                <th style={{ textAlign: "right" }}>Gaji Bulanan</th>
                <th style={{ textAlign: "right" }}>Tunjangan</th>
                <th style={{ textAlign: "right" }}>Potongan</th>
                <th style={{ textAlign: "right" }}>Gaji Bersih</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Dibayar</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id}>
                  <td>{r.no_karyawan}</td>
                  <td>{r.nama_lengkap}</td>
                  <td style={{ textAlign: "right" }}>{fmt(r.gaji_bulanan)}</td>
                  <td style={{ textAlign: "right", color: "#003399" }}>{fmt(r.total_tunjangan)}</td>
                  <td style={{ textAlign: "right", color: "#d8000c" }}>{fmt(r.total_potongan)}</td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(r.gaji_bersih)}</td>
                  <td>
                    {r.payment_status === "Terbayar" ? (
                      <span className="vb-badge vb-badge-approved">Terbayar</span>
                    ) : (
                      <span className="vb-badge vb-badge-pending">Pending</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right", color: r.amount_paid ? "#4f8a10" : "#999" }}>
                    {r.amount_paid ? fmt(r.amount_paid) : "-"}
                    {r.payment_date && <div style={{ fontSize: 10, color: "#666" }}>{r.payment_date}</div>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => setSlipView(r)} data-testid={`view-slip-${r.employee_id}`}>
                      <Eye className="w-3 h-3 inline" />
                    </button>
                    {r.payment_status === "Terbayar" ? (
                      <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setCancelTarget(r)} data-testid={`cancel-pay-${r.employee_id}`}>
                        <X className="w-3 h-3 inline" />
                      </button>
                    ) : (
                      <button className="vb-btn-primary" style={{ padding: "2px 6px" }} onClick={() => openPayDialog(r)} data-testid={`pay-${r.employee_id}`}>
                        <Wallet className="w-3 h-3 inline mr-1" />Bayar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pay Dialog */}
      <Dialog
        open={!!payTarget}
        onClose={() => setPayTarget(null)}
        title="Konfirmasi Pembayaran Gaji"
        width={520}
        footer={
          <>
            <button className="vb-btn" onClick={() => setPayTarget(null)}>Batal</button>
            <button className="vb-btn-primary" onClick={submitPay} data-testid="confirm-pay">
              <Wallet className="w-3 h-3 inline mr-1" />Bayarkan
            </button>
          </>
        }
      >
        {payTarget && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, fontSize: 12, marginBottom: 12 }}>
              <strong>{payTarget.nama_lengkap}</strong> ({payTarget.no_karyawan})<br />
              Periode: <strong>{bulan}</strong><br />
              Gaji Bersih (kalkulasi): <strong style={{ color: "#4f8a10" }}>{fmt(payTarget.gaji_bersih)}</strong>
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Jumlah Dibayarkan*</label>
              <RupiahInput value={payForm.amount_paid} onChange={(v) => setPayForm({ ...payForm, amount_paid: v })} testId="pay-amount" />
              {payForm.amount_paid !== payTarget.gaji_bersih && (
                <div style={{ fontSize: 11, color: "#9F6000", marginTop: 2 }}>
                  ⚠ Selisih: {fmt(payForm.amount_paid - payTarget.gaji_bersih)}
                </div>
              )}
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Tanggal Bayar*</label>
              <input type="date" className="vb-input" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} data-testid="pay-date" />
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Metode*</label>
              <select className="vb-select" value={payForm.payment_method} onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })} data-testid="pay-method">
                <option value="Transfer">Transfer ke {payTarget.bank_nama || "Bank"} - {payTarget.bank_rekening || "-"}</option>
                <option value="Cash">Cash / Tunai</option>
              </select>
            </div>
            <div className="vb-form-row">
              <label className="vb-label">Catatan</label>
              <textarea className="vb-textarea" rows={2} value={payForm.catatan} onChange={(e) => setPayForm({ ...payForm, catatan: e.target.value })} placeholder="No. referensi transfer, dll" />
            </div>
            <div style={{ fontSize: 11, color: "#003399", padding: 6, background: "#e8f4ff", border: "1px solid #c4d9f1", marginTop: 8 }}>
              ℹ Pembayaran akan otomatis dicatat di Arus Kas Keluar (kategori "Penggajian")
            </div>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={cancelPay}
        title="Batalkan Pembayaran"
        message={`Yakin batalkan pembayaran ${cancelTarget?.nama_lengkap} (${fmt(cancelTarget?.amount_paid || 0)})? Entri di Arus Kas juga akan dihapus.`}
      />
    </Window>
  );
}
